import React, { useEffect, useState, useCallback } from "react";
import clsx from "clsx";
import { Link } from "react-router-dom";
import Layout from "../layouts";
import axios, { apiBaseUrl } from "../services/http";
import { ToastContainer, toast } from "react-toastify";
import {useAccountStore} from '../store';

export default function CheckIn() {
  const { fetch: fetchUser } = useAccountStore();
  const [booking, setBooking] = useState({
    status: "idle",
    data: null,
    error: null,
  });
  const [submit, setSubmit] = useState({
    status: "succeeded",
    data: null,
    error: null,
  });

  const fetchBooking = useCallback(async () => {
    setBooking((state) => ({ ...state, status: "pending", error: null }));
    try {
      const response = await axios.get(
        `${apiBaseUrl}/api/check-in`
      );
      setBooking((state) => ({
        ...state,
        status: "succeeded",
        data: response.data,
      }));
    } catch (error) {
      setBooking((state) => ({
        ...state,
        status: "failed",
        error: error.response.data,
      }));
    }
  }, []);

  useEffect(() => {
    fetchBooking();
  }, [fetchBooking]);

  const onCheckIn = async () => {
    if (!booking.data.id) {
      return;
    }
    setSubmit((state) => ({ status: "pending", data: null, error: null }));
    try {
      await axios.post(`${apiBaseUrl}/api/check-in/${booking.data.id}`);
      setSubmit((state) => ({ status: "succeeded", data: null, error: null }));
      fetchBooking();
      fetchUser();
      toast.success('Check-in complete.');
    } catch (error) {
      setSubmit((state) => ({ status: "failed", data: null, error: null }));
    }
  }

  return (
    <Layout>
      <div className="container mx-auto">
        <div className="lg:py-5 lg:px-0 py-4 px-4">
          <h1 className="font-bold text-lg mb-4">
            Check in your booking session
          </h1>

          {booking.error && (
            <div className="flex flex-col items-center justify-content space-y-5">
              <svg
                className="w-20 h-20 text-gray-400"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                ></path>
              </svg>
              <h3 className="font-bold text-lg">Use a Check-in Station</h3>
              <p className="text-gray-500">
                You can only check in your booking session with a check-in
                station or any public PCs in the library.
              </p>
              <p className="text-gray-500">
                If you need assistance, please contact us on 2948-6653 or
                libinfo@eduhk.hk.
              </p>
            </div>
          )}

          {booking.data && (
            <div className="space-y-10">
              <div className="space-y-8 flex flex-col items-center justify-center">
                <img
                  class="w-auto h-48 object-cover"
                  src={booking.data.space.thumbnail}
                  alt=""
                />
                <table className="table-fixed w-full max-w-xl border-collapse border">
                  <tr>
                    <td className="border px-4 py-4">
                      <div className="text-center">
                        <label className="text-gray-500 text-xs">
                          Facility
                        </label>
                        <h3 className="font-semibold text-lg">
                          {booking.data.space.name}
                        </h3>
                      </div>
                    </td>
                    <td className="border px-4 py-4">
                      <div className="text-center">
                        <label className="text-gray-500 text-xs">
                          Room / Seat
                        </label>
                        <h3 className="font-semibold text-lg">
                          {booking.data.room.name}
                        </h3>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-4">
                      <div className="text-center">
                        <label className="text-gray-500 text-xs">Date</label>
                        <h3 className="font-semibold text-lg">
                          {booking.data.date}
                        </h3>
                      </div>
                    </td>
                    <td className="border px-4 py-4">
                      <div className="text-center">
                        <label className="text-gray-500 text-xs">Session</label>
                        <h3 className="font-semibold text-lg">
                          {booking.data.session}
                        </h3>
                      </div>
                    </td>
                  </tr>
                  <tr>
                    <td className="border px-4 py-4">
                      <div className="text-center">
                        <label className="text-gray-500 text-xs">
                          Booking Type
                        </label>
                        <h3 className="font-semibold text-lg">
                          {booking.data.type === "group"
                            ? "Group"
                            : "Individual"}
                        </h3>
                      </div>
                    </td>
                    <td className="border px-4 py-4">
                      <div className="text-center">
                        <label className="text-gray-500 text-xs">Status</label>
                        <h3 className="font-semibold text-lg">
                          Pending for check-in
                        </h3>
                        {booking.data.require_add_users > 0 && (
                          <div>
                            <span className="inline-flex items-center text-xs text-red-600">
                              <svg
                                className="w-5 h-5 mr-1"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                viewBox="0 0 24 24"
                                xmlns="http://www.w3.org/2000/svg"
                                aria-hidden="true"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"
                                ></path>
                              </svg>
                              Requires {booking.data.require_add_users} more
                              user(s) to check in.
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                </table>

                {booking.data.type === "group" &&
                  booking.data.has_checkedin && (
                    <div className="text-red-600">
                      <svg
                        className="w-6 h-6 inline-block mr-1"
                        fill="none"
                        stroke="currentColor"
                        stroke-width="1.5"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                        aria-hidden="true"
                      >
                        <path
                          stroke-linecap="round"
                          stroke-linejoin="round"
                          d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
                        ></path>
                      </svg>
                      Your booking status will change to "Checked in" after the
                      total number of checked-in users reaches the minimum
                      seating capacity of that room.
                    </div>
                  )}
                {!booking.data.has_checkedin && (
                  <div className="flex items-center space-x-4">
                    <button
                      disabled={submit.status === "pending"}
                      className={clsx(
                        "text-white bg-orange-500 hover:bg-orange-600 focus:ring-4 focus:outline-none focus:ring-orange-300 rounded-lg px-8 py-3 text-center",
                        submit.status === "pending"
                          ? "opacity-50 pointer-events-none"
                          : ""
                      )}
                      onClick={onCheckIn}
                    >
                      Check In
                    </button>
                  </div>
                )}
                <p className="text-gray-500 text-sm">
                  * To cancel your booking, go to{" "}
                  <Link
                    to="/mybookings"
                    className="text-orange-500 hover:underline"
                  >
                    My Bookings
                  </Link>
                  .
                </p>
              </div>
            </div>
          )}

          {/* {submit.status === "succeeded" && (
            <div className="flex flex-col items-center justify-content space-y-5">
              <svg
                className="w-20 h-20 text-green-400"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                ></path>
              </svg>
              <h3 className="font-bold text-lg">
                Check-in complete
              </h3>
            </div>
          )} */}

          {booking.status === "succeeded" && !booking.data && (
            <div className="flex flex-col items-center justify-content space-y-5">
              <svg
                className="w-20 h-20 text-gray-400"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
                ></path>
              </svg>
              <h3 className="font-bold text-lg">
                No booking session available for check in
              </h3>
              <p className="text-gray-500">
                Please see{" "}
                <Link
                  to="/mybookings"
                  className="text-orange-500 hover:underline"
                >
                  My Bookings
                </Link>{" "}
                for all your active / upcoming bookings.
              </p>
            </div>
          )}
        </div>
      </div>
      <ToastContainer
        position="bottom-right"
        hideProgressBar={true}
        closeButton={false}
        limit={1}
      />
    </Layout>
  );
}
